import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import FormControl from '@mui/material/FormControl';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTheme } from '@mui/material/styles';
import { startOfDay } from 'date-fns';
import { toPickerValue } from '../DateDisplay/dates';

// ISO instants, and bare 'yyyy-MM-dd' days (which name their day and must not be wiped).
function isISO8601(dateString) {
  const regex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(([+-]\d{2}:\d{2})|Z)?)?$/;
  return regex.test(dateString);
}

export default function DateSelect(props) {
  // keepTime: the bound field is a real moment (signature date, schedule time, a report bound),
  // not a business day, so the picked value keeps its time of day.
  const { label, onChange, value, helperText, error, disabled, required = false, requiredComplete = false, keepTime = false } = props;
  const dataCy = props['data-cy'];
  const theme = useTheme();
  const invalidValueRef = useRef(false);

  const dateFormat = useSelector(state => state.auth.user.dateFormat);
  let newFormat = 'd/M/yyyy';
  if(dateFormat != null && dateFormat !== '') {
    newFormat = dateFormat.replace('mm', 'MM');
  }

  let dateValue = null;
  if(value != null && value !== ''){
    // Stored values open on the day they name in the profile timezone; what the picker
    // itself emitted (a Date) comes back unchanged — see UI/DateDisplay/dates.
    if(isISO8601(value) || value instanceof Date || (typeof value === 'number' && !isNaN(value))) {
        dateValue = toPickerValue(value);
    }else {
        dateValue = null;
        invalidValueRef.current = true;
    }
  }

  // Handle invalid values in useEffect to avoid setState during render
  useEffect(() => {
    if (invalidValueRef.current) {
      invalidValueRef.current = false;
      onChange(null);
    }
  }, [value, onChange]);

  // MUI changes only the day and keeps the value's time of day, so a value loaded with a time
  // (a default of now, a stored instant) would send that time on with the new day — and 18:00
  // in Edmonton is already tomorrow in UTC. A date-only picker always emits local midnight.
  // Values the screen hands in are not rewritten here: load stored dates with pickerDay() and
  // default with pickerToday() (UI/DateDisplay/dates) so the form holds a plain day from the start.
  const handleChange = (picked, context) => {
    const plainDay = !keepTime && picked instanceof Date && !isNaN(picked.getTime());
    onChange(plainDay ? startOfDay(picked) : picked, context);
  };

  return (
    <FormControl fullWidth>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          closeOnSelect
          enableAccessibleFieldDOMStructure={false}
          format={newFormat}
          id={"date-picker-inline-" + label}
          name={"date-picker-inline-" + label}
          label={label}
          value={dateValue}
          // A cleared field has no time to keep, and MUI builds the next pick from today rounded
          // to the format's smallest section — midnight for a date-only format. Real moments
          // take the time of day from now instead. Only consulted while the value is empty.
          referenceDate={keepTime ? new Date() : undefined}
          onChange={handleChange}
          disabled={disabled}
          slotProps={{
            textField: {
              'data-cy': dataCy,
              helperText: helperText,
              variant: 'standard',
              required: required || requiredComplete,
              error: error,
              clearable: true,
              onClear: () => onChange(null),
              sx: {
                '& .MuiInputBase-input': { fontSize: '0.8rem' },
                '& .MuiInputLabel-root': { fontSize: '0.8rem' },
                '& .MuiFormLabel-asterisk': {
                  color: requiredComplete ? theme.palette.error.main : theme.palette.primary.main,
                },
              },
            },
            openPickerIcon: {
              style: { color: error ? theme.palette.error.main : 'grey' }
            }
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );  
};